import React, { useState } from "react";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import SportPlayerPicker from "../components/community/SportPlayerPicker";
import { useCreateCommunityDebateMutation } from "../store/api/communityApi";
import { useSportWorkspace } from "../hooks/useSportWorkspace";
import { useAnalytics } from "../hooks/useAnalytics";
import { brandFilledButtonSx } from "../utils/brandAssets";
import { communityDebatePath, playerSearchName } from "../utils/community";

const CandidateField = ({ label, player, onPick }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Button variant="outlined" fullWidth sx={{ mt: 0.5, justifyContent: "flex-start" }} onClick={onPick}>
      {player ? playerSearchName(player) : "Pick a player"}
    </Button>
  </Box>
);

const CommunityDebateCreate = () => {
  const { sport } = useSportWorkspace();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { trackCustomEvent } = useAnalytics();
  const [question, setQuestion] = useState("Who had the greater peak?");
  const [a, setA] = useState(params.get("a") ? { playerId: params.get("a") } : null);
  const [b, setB] = useState(params.get("b") ? { playerId: params.get("b") } : null);
  const [picker, setPicker] = useState(null);
  const [error, setError] = useState("");
  const [createDebate, { isLoading }] = useCreateCommunityDebateMutation();

  const handleCreate = async () => {
    try {
      const debate = await createDebate({
        sport,
        question,
        candidateIds: [a?.playerId, b?.playerId],
      }).unwrap();
      trackCustomEvent("debate_created", { sport, debate_id: debate.id });
      navigate(communityDebatePath(sport, debate.slug || debate.id));
    } catch (err) {
      setError(err?.data?.error || "Could not create debate");
    }
  };

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 640, mx: "auto" }}>
      <Typography variant="overline" color="text.secondary">
        Create debate
      </Typography>
      <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5, mb: 3 }}>
        Turn a take into a vote
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TextField
        label="Question"
        fullWidth
        value={question}
        onChange={(event) => setQuestion(event.target.value)}
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "grid", gap: 2, mb: 3 }}>
        <CandidateField label="Candidate A" player={a} onPick={() => setPicker("a")} />
        <CandidateField label="Candidate B" player={b} onPick={() => setPicker("b")} />
      </Box>
      <Button
        variant="contained"
        sx={brandFilledButtonSx}
        disabled={isLoading || !a?.playerId || !b?.playerId}
        onClick={handleCreate}
      >
        Publish debate
      </Button>
      <SportPlayerPicker
        open={Boolean(picker)}
        sport={sport}
        title={picker === "a" ? "Pick candidate A" : "Pick candidate B"}
        excludePlayerIds={[a?.playerId, b?.playerId].filter(Boolean)}
        onClose={() => setPicker(null)}
        onSelect={(player) => {
          if (picker === "a") setA(player);
          if (picker === "b") setB(player);
          setPicker(null);
        }}
      />
    </Box>
  );
};

export default CommunityDebateCreate;
